import { Link } from "react-router";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { BonificoOrderStatus } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { SalesGateway } from "./SaleRecord.ts";
import { BonificoPayments } from "./salesBreakdowns.ts";
import { ordersListPathFor } from "./ordersListLinks.ts";
import { formatCount, formatEuros } from "./salesFormat.ts";
import {
  EmptyStatistics,
  LoadingBlock,
  Stat,
  StatisticsCard,
} from "./StatisticsCard.tsx";

const AWAITING_TRANSFER_ORDERS_PATH = ordersListPathFor({
  gateway: SalesGateway.Bonifico,
  statuses: [
    BonificoOrderStatus.PendingPayment,
    BonificoOrderStatus.PartiallyPaid,
  ],
});

const DRAFT_ORDERS_PATH = ordersListPathFor({
  gateway: SalesGateway.Bonifico,
  statuses: [BonificoOrderStatus.Draft],
});

function OrdersLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className="text-xs text-blue-600 hover:underline">
      {children}
    </Link>
  );
}

const daysFormat = new Intl.NumberFormat("it-IT", {
  maximumFractionDigits: 1,
});

export function BonificoPaymentsCard({
  payments,
  isLoading,
}: {
  payments: BonificoPayments;
  isLoading: boolean;
}) {
  const hasData =
    payments.paidOrderCount > 0 ||
    payments.awaitingTransferCount > 0 ||
    payments.draftCount > 0;

  return (
    <StatisticsCard
      title="Bonifici"
      info={SALES_DEFINITIONS.bonifici}
      description="Tempi di pagamento nel periodo e ordini ancora aperti ad oggi"
    >
      {isLoading ? (
        <LoadingBlock className="h-24 w-full" />
      ) : !hasData ? (
        <EmptyStatistics>Nessun ordine con bonifico</EmptyStatistics>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat
            label="Tempo medio di pagamento"
            value={
              payments.averageDaysToPay === null
                ? "—"
                : `${daysFormat.format(payments.averageDaysToPay)} giorni`
            }
          >
            <span className="text-xs text-gray-500">
              Dalla conferma dell'ordine alla data valuta, su{" "}
              {formatCount(payments.paidOrderCount)} bonifici ricevuti
            </span>
          </Stat>
          <Stat
            label="In attesa di bonifico"
            value={formatEuros(payments.awaitingTransferInCents)}
          >
            <span className="text-xs text-gray-500">
              {formatCount(payments.awaitingTransferCount)} ordini confermati,
              B2B compresi per la parte da pagare
            </span>
            {payments.awaitingTransferCount > 0 && (
              <OrdersLink to={AWAITING_TRANSFER_ORDERS_PATH}>
                Vedi gli ordini in attesa
              </OrdersLink>
            )}
          </Stat>
          <Stat
            label="Bozze da confermare"
            value={formatEuros(payments.draftsInCents)}
          >
            <span className="text-xs text-gray-500">
              {formatCount(payments.draftCount)} bozze in attesa del cliente
            </span>
            {payments.draftCount > 0 && (
              <OrdersLink to={DRAFT_ORDERS_PATH}>Vedi le bozze</OrdersLink>
            )}
          </Stat>
        </div>
      )}
    </StatisticsCard>
  );
}
